import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { api, BookingQuoteRequest, QuoteRequestWorkflowStatus } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';

const STATUS_LABELS: Record<QuoteRequestWorkflowStatus, string> = {
  pending_quote: 'New',
  quoted: 'Quoted',
  declined: 'Declined',
  expired: 'Expired',
  converted: 'Converted',
};

function dateTime(value: string) {
  return new Date(value).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function timeRange(request: BookingQuoteRequest) {
  const start = new Date(request.requestedStartTime);
  const end = new Date(request.requestedEndTime);
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: request.venueTimezone || 'UTC' };
  const formattedStart = start.toLocaleString('en-US', options);
  const formattedEnd = end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: request.venueTimezone || 'UTC' });
  return `${formattedStart}–${formattedEnd}${request.venueTimezone ? ` (${request.venueTimezone})` : ' (venue time)'}`;
}

function statusOf(request: BookingQuoteRequest): QuoteRequestWorkflowStatus {
  return request.workflowStatus || request.status || 'pending_quote';
}

export default function SuperAdminQuoteRequestsScreen() {
  const { isSuperAdmin } = useAuth();
  const { showAlert } = useModal();
  const [requests, setRequests] = useState<BookingQuoteRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<QuoteRequestWorkflowStatus>('pending_quote');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getBookingQuoteRequests(statusFilter);
      setRequests(response.requests || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Quote requests could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { if (isSuperAdmin) load(); }, [isSuperAdmin, load]);

  const update = async (request: BookingQuoteRequest, status: QuoteRequestWorkflowStatus) => {
    if (busyId) return;
    setBusyId(request.id);
    const note = window.prompt(status === 'declined' ? 'Reason for declining this quote request:' : 'Add an internal note (optional):', request.note || '');
    if (note === null || (status === 'declined' && !note.trim())) { setBusyId(null); return; }
    let quoteAmountCents: number | undefined;
    let quoteTaxCents: number | undefined;
    if (status === 'quoted') {
      const amount = window.prompt('Verified venue quote in dollars (customer is not charged by this action):', '');
      if (amount === null || !amount.trim() || !Number.isFinite(Number(amount)) || Number(amount) <= 0) {
        await showAlert('Verified price required', 'Enter a positive dollar amount from the venue before marking this request quoted.', 'error');
        setBusyId(null); return;
      }
      quoteAmountCents = Math.round(Number(amount) * 100);
      const tax = window.prompt('Verified tax in dollars (optional):', '0');
      if (tax === null) { setBusyId(null); return; }
      if (tax.trim() && (!Number.isFinite(Number(tax)) || Number(tax) < 0)) {
        await showAlert('Invalid tax', 'Enter zero or a positive tax amount.', 'error');
        setBusyId(null); return;
      }
      if (tax.trim()) quoteTaxCents = Math.round(Number(tax) * 100);
    }
    try {
      await api.updateBookingQuoteRequest(request.id, { status, staffNote: note.trim() || undefined, quoteAmountCents, quoteTaxCents });
      await load();
    } catch (err) {
      await showAlert('Could not update request', err instanceof Error ? err.message : 'Please try again.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const saveNote = async (request: BookingQuoteRequest) => {
    if (busyId) return;
    setBusyId(request.id);
    const note = window.prompt('Internal staff note:', request.note || '');
    if (note === null) { setBusyId(null); return; }
    try {
      await api.updateBookingQuoteRequest(request.id, { status: 'pending_quote', staffNote: note.trim() });
      await load();
    } catch (err) {
      await showAlert('Could not save note', err instanceof Error ? err.message : 'Please try again.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const openCount = useMemo(() => requests.filter(item => statusOf(item) === 'pending_quote').length, [requests]);

  if (!isSuperAdmin) return <DashboardLayout><div className="card"><h2>Quote requests</h2><p>Super admin access is required.</p></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div style={{ display: 'grid', gap: 20 }}>
        <section className="card" style={{ display: 'grid', gap: 10 }}>
          <div style={{ color: '#64748b', fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Staff follow-up</div>
          <h1 style={{ margin: 0, color: '#0f172a' }}>Free quote requests <span style={{ color: '#64748b', fontSize: 18, fontWeight: 600 }}>({openCount} open)</span></h1>
          <p style={{ margin: 0, color: '#475569', maxWidth: 760 }}>These are customer inquiries for a researched price. They are not reservations, do not hold court time, and do not charge the customer. Contact the customer manually after checking the venue.</p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <label htmlFor="quote-status-filter" style={{ color: '#475569', fontWeight: 700 }}>Show</label>
            <select id="quote-status-filter" value={statusFilter} onChange={event => setStatusFilter(event.target.value as QuoteRequestWorkflowStatus)}>
              {(['pending_quote', 'quoted', 'declined', 'expired', 'converted'] as QuoteRequestWorkflowStatus[]).map(value => <option key={value} value={value}>{STATUS_LABELS[value]}</option>)}
            </select>
            <button type="button" className="btn btn-secondary" onClick={load} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
          </div>
        </section>

        {error && <div className="card" role="alert" style={{ color: '#b91c1c' }}>{error}</div>}
        {!loading && !error && requests.length === 0 && <div className="card"><h2>No quote requests</h2><p>New non-binding inquiries will appear here.</p></div>}
        {requests.map(request => {
          const status = statusOf(request);
          const closed = status !== 'pending_quote';
          const customer = `${request.customerFirstName} ${request.customerLastName || ''}`.trim();
          return (
            <section className="card" key={request.id} style={{ display: 'grid', gap: 16, opacity: closed ? 0.78 : 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                <div>
                  <h2 style={{ margin: 0 }}>{request.venueName}</h2>
                  <div style={{ color: '#475569', marginTop: 5 }}>{request.courtName} · {timeRange(request)}</div>
                </div>
                <span style={{ alignSelf: 'flex-start', borderRadius: 999, padding: '6px 10px', background: '#f1f5f9', color: '#334155', fontSize: 12, fontWeight: 800 }}>{STATUS_LABELS[status] || status}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))', gap: 12, color: '#334155' }}>
                <div><strong>Customer</strong><br />{customer}<br /><a href={`mailto:${request.customerEmail}`}>{request.customerEmail}</a>{request.customerPhone && <><br /><a href={`tel:${request.customerPhone}`}>{request.customerPhone}</a></>}</div>
                <div><strong>Received</strong><br />{dateTime(request.createdAt)}<br /><span style={{ color: '#64748b' }}>Currency: {request.currency}</span></div>
              </div>
              {(request.quoteAmountCents != null || request.quoteTaxCents != null || request.quoteExpiresAt) && <div style={{ padding: 12, background: '#f0fdf4', borderRadius: 8, color: '#166534' }}><strong>Saved quote:</strong> {request.quoteAmountCents != null ? `$${(request.quoteAmountCents / 100).toFixed(2)}` : '—'}{request.quoteTaxCents != null ? ` + $${(request.quoteTaxCents / 100).toFixed(2)} tax` : ''}{request.quoteExpiresAt ? ` · expires ${dateTime(request.quoteExpiresAt)}` : ''}</div>}
              {request.note && <div style={{ padding: 12, background: '#f8fafc', borderRadius: 8, color: '#475569' }}><strong>Staff note:</strong> {request.note}</div>}
              {!closed && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-secondary" disabled={busyId === request.id} onClick={() => saveNote(request)}>Save staff note</button>
                <button type="button" className="btn btn-primary" disabled={busyId === request.id} onClick={() => update(request, 'quoted')}>Save verified quote</button>
                <button type="button" className="btn btn-secondary" disabled={busyId === request.id} onClick={() => update(request, 'declined')}>Decline request</button>
              </div>}
            </section>
          );
        })}
      </div>
    </DashboardLayout>
  );
}
